// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// layout.ts — board geometry and hit testing, as pure arithmetic with no DOM in sight, because
// this is where the phone bugs live and jsdom cannot see a layout (principle #20).
//
// THE HARD PROBLEM THIS FILE SOLVES. A hedge does not sit in a cell, it sits on the lattice
// BETWEEN cells. On a 375px phone a 9x9 common gives ~36px cells and a lattice line about 6px
// wide — so "tap the gap between two squares" is an unhittable target and would be the single
// worst control in the fleet.
//
// The answer is that NOTHING is hit-tested by its own bounds. Every tap resolves to the NEAREST
// LEGAL ACTION, measured to the action's centre (a cell) or to the nearest point on its segment
// (a hedge). Three things follow, and all three are what make it work under a thumb:
//   * there is no dead zone anywhere on the board — every pixel means something;
//   * an ILLEGAL action can never be aimed at, because only legal ones are ever in the candidate
//     list, so the "you may not seal someone in" rule is enforced by the geometry rather than by
//     an error message;
//   * the effective target is as large as the gap to its nearest rival candidate, which for pawn
//     steps (never adjacent to each other) is around two cells — comfortably past the 44px bar
//     even though a cell is drawn at 36px.
// Aiming is then confirmed with a full-width button, so a mis-aimed hedge costs a glance, not a
// hedge.

import { orientOf, postOf, FLAT, type Cell, type Hedge } from './rules';

export interface Layout {
  /** Cells per side. */
  readonly size: number;
  /** Side of one cell, in CSS px. */
  readonly cell: number;
  /** The lattice gap between two cells — also the drawn thickness of a hedge. */
  readonly gap: number;
  /** Side of the whole board, in CSS px. */
  readonly board: number;
}

export const MIN_CELL = 26;
export const MAX_CELL = 62;

/**
 * Fit the board to the box the screen actually offers. The board is square, so it takes the
 * smaller dimension; the caller centres it. Sizing to the SMALLER of the two is what keeps a
 * landscape phone from producing a board taller than the viewport.
 */
export function computeLayout(size: number, availW: number, availH: number): Layout {
  const gap = size >= 9 ? 5 : 6;
  const usable = Math.max(120, Math.min(availW, availH));
  // size cells + (size - 1) gaps + a hair of padding must fit inside `usable`
  const raw = (usable - gap * (size - 1)) / size;
  const cell = Math.max(MIN_CELL, Math.min(MAX_CELL, Math.floor(raw)));
  return { size, cell, gap, board: cell * size + gap * (size - 1) };
}

const step = (l: Layout): number => l.cell + l.gap;

/** Top-left corner of a cell, in board-local px. */
export function cellXY(l: Layout, cell: Cell): { x: number; y: number } {
  const r = (cell / l.size) | 0;
  const c = cell % l.size;
  return { x: c * step(l), y: r * step(l) };
}

export function cellCentre(l: Layout, cell: Cell): { x: number; y: number } {
  const { x, y } = cellXY(l, cell);
  return { x: x + l.cell / 2, y: y + l.cell / 2 };
}

/**
 * The drawn rectangle of a hedge, in board-local px. A hedge spans two cells plus the gap between
 * them, and lies IN the lattice gap — which is why its thickness is `gap` and not `cell`.
 */
export function hedgeRect(l: Layout, h: Hedge): { x: number; y: number; w: number; h: number } {
  const span = l.size - 1;
  const post = postOf(h);
  const r = (post / span) | 0;
  const c = post % span;
  const long = l.cell * 2 + l.gap;
  if (orientOf(h) === FLAT) {
    return { x: c * step(l), y: r * step(l) + l.cell, w: long, h: l.gap };
  }
  return { x: c * step(l) + l.cell, y: r * step(l), w: l.gap, h: long };
}

export function hedgeCentre(l: Layout, h: Hedge): { x: number; y: number } {
  const r = hedgeRect(l, h);
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/** Squared distance from a point to a rectangle (0 inside it). */
function distToRect(px: number, py: number, x: number, y: number, w: number, h: number): number {
  const dx = px < x ? x - px : px > x + w ? px - (x + w) : 0;
  const dy = py < y ? y - py : py > y + h ? py - (y + h) : 0;
  return dx * dx + dy * dy;
}

/**
 * The legal hedge nearest a tap. Measured to the hedge's SEGMENT, not to its post, so aiming at
 * the middle of a two-cell span picks that span rather than whichever post happens to be closer —
 * and so orientation falls out of where the finger is instead of needing a mode switch.
 */
export function nearestHedge(l: Layout, legal: readonly Hedge[], px: number, py: number): Hedge | null {
  let best: Hedge | null = null;
  let bestD = Infinity;
  for (const h of legal) {
    const r = hedgeRect(l, h);
    // Widen the segment by half a gap on every side so the thin axis is never the reason a hedge
    // loses a tie to its neighbour: the comparison should be about WHICH span you meant.
    const pad = l.gap;
    const d = distToRect(px, py, r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2);
    if (d < bestD) {
      bestD = d;
      best = h;
    }
  }
  return best;
}

/** The legal step nearest a tap, or null when the tap is further away than `maxPx` from all of
 *  them — which is what stops a tap on the far side of the board from teleporting the pawn. */
export function nearestStep(l: Layout, legal: readonly Cell[], px: number, py: number, maxPx = 999): Cell | null {
  let best: Cell | null = null;
  let bestD = Infinity;
  for (const cell of legal) {
    const c = cellCentre(l, cell);
    const d = (c.x - px) ** 2 + (c.y - py) ** 2;
    if (d < bestD) {
      bestD = d;
      best = cell;
    }
  }
  return best !== null && bestD <= maxPx * maxPx ? best : null;
}

/**
 * The smallest gap between any two legal step targets, in px — i.e. the real effective tap target
 * once nearest-wins hit testing is applied.
 */
export function effectiveTargetPx(l: Layout, legal: readonly Cell[]): number {
  if (legal.length <= 1) return Infinity;
  let min = Infinity;
  for (let i = 0; i < legal.length; i++) {
    for (let j = i + 1; j < legal.length; j++) {
      const a = cellCentre(l, legal[i]);
      const b = cellCentre(l, legal[j]);
      min = Math.min(min, Math.hypot(a.x - b.x, a.y - b.y));
    }
  }
  return min;
}

/** The comfortable-thumb floor from MOBILE_CONTROLS §6. */
export const TOUCH_FLOOR = 44;

/**
 * Does choosing `chosen` need a confirmation step, because another legal step sits close enough
 * that a thumb could plausibly have meant that one instead?
 *
 * THIS EXISTS BECAUSE THE 44px RULE CANNOT BE MET BY GEOMETRY HERE, and pretending otherwise would
 * be the lie. Nine cells plus eight gaps do not fit in 375px at 44px a cell — the board would have
 * to be 436px wide — so on the largest common a cell is drawn at ~36px. Usually that is harmless:
 * the legal steps around a pawn are two cells apart, so the effective target is ~80px. But the
 * jump-and-bend rule breaks that assumption. With a rival adjacent and the square behind it hedged
 * off, the bend targets sit DIAGONALLY next to the ordinary side-steps, and two legal targets end
 * up one cell apart — measured at 38px, under the floor.
 *
 * Rather than ship a 38px target, the ambiguous case asks. A tap that is unambiguous still moves
 * the pawn on the first tap; only the rare crowded case costs a second tap, on a full-width button.
 * tests/layout.test.ts asserts the pairing directly: every step set whose effective target falls
 * below the floor must be one this function flags.
 */
export function needsConfirm(l: Layout, legal: readonly Cell[], chosen: Cell): boolean {
  const c = cellCentre(l, chosen);
  for (const other of legal) {
    if (other === chosen) continue;
    const o = cellCentre(l, other);
    if (Math.hypot(o.x - c.x, o.y - c.y) < TOUCH_FLOOR) return true;
  }
  return false;
}
